import axios from "axios";
import { purgeLegacyAuthCredentials, clearAuthHint } from "@/lib/authHint";

// Stockaily — EPF v1.5 backend URL resolution.
//
// Resolution order (first non-empty wins):
//   a. window.__STOCKAILY_RUNTIME_CONFIG__.backendUrl
//      — populated by /runtime-config.js which is generated inside
//        the Docker builder stage from `REACT_APP_BACKEND_URL` and
//        served synchronously by nginx BEFORE main.*.js loads.
//        Immune to any CRA/dotenv env-substitution quirk.
//   b. process.env.REACT_APP_BACKEND_URL
//      — CRA-substituted at build time by webpack DefinePlugin. Used
//        by Preview (`yarn start`), where the runtime-config stub
//        ships with backendUrl:"" and this branch takes over.
//   c. "" (empty) → API falls back to "/api" (same origin).
//      — Correct for the Emergent Preview environment whose
//        Kubernetes ingress routes `/api/*` to the backend on the
//        same origin (documented, expected same-origin fallback).
function _resolveBackendUrl() {
  try {
    if (
      typeof window !== "undefined" &&
      window.__STOCKAILY_RUNTIME_CONFIG__ &&
      typeof window.__STOCKAILY_RUNTIME_CONFIG__.backendUrl === "string" &&
      window.__STOCKAILY_RUNTIME_CONFIG__.backendUrl.trim() !== ""
    ) {
      return {
        raw: window.__STOCKAILY_RUNTIME_CONFIG__.backendUrl.trim(),
        source: "runtime_config",
      };
    }
  } catch (_e) {
    /* fall through to env */
  }
  const fromEnv =
    (typeof process !== "undefined" &&
      process.env &&
      process.env.REACT_APP_BACKEND_URL) ||
    "";
  if (fromEnv) {
    return { raw: fromEnv, source: "process_env" };
  }
  return { raw: "", source: "unresolved" };
}

const _resolved = _resolveBackendUrl();
const RAW_BACKEND_URL = _resolved.raw;
const BACKEND_URL = RAW_BACKEND_URL.replace(/\/$/, "");
export const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";
export const BACKEND_URL_RESOLVED = BACKEND_URL || "(empty — same origin)";
export const BACKEND_URL_MISSING = !BACKEND_URL;
export const BACKEND_URL_SOURCE = _resolved.source;

if (typeof window !== "undefined") {
  window.__STOCKAILY_BACKEND_URL__ = BACKEND_URL_RESOLVED;
  window.__STOCKAILY_BACKEND_URL_MISSING__ = BACKEND_URL_MISSING;
  window.__STOCKAILY_BACKEND_URL_SOURCE__ = BACKEND_URL_SOURCE;
  if (BACKEND_URL_MISSING) {
    // eslint-disable-next-line no-console
    console.error(
      "[stockaily.api] CRITICAL — backend URL is empty. " +
      "All /api calls will go to the SAME origin (" + window.location.origin + "). " +
      "Neither window.__STOCKAILY_RUNTIME_CONFIG__.backendUrl nor " +
      "process.env.REACT_APP_BACKEND_URL was set."
    );
  } else {
    // eslint-disable-next-line no-console
    console.info(
      "[stockaily.api] BACKEND_URL=%s API=%s source=%s origin=%s",
      BACKEND_URL_RESOLVED, API, BACKEND_URL_SOURCE, window.location.origin
    );
  }
}

// WS-8 — cookie-only auth. `withCredentials: true` ensures the
// ``session_token`` HttpOnly cookie is sent on every /api call.
const api = axios.create({ baseURL: API, withCredentials: true });

// WS-5 v3 (2026-08-09) — CSRF token in-memory fallback cache.
//
// The double-submit CSRF pattern requires the frontend to echo the
// server-set ``stockaily_csrf`` cookie in the ``X-CSRF-Token``
// request header. When the cookie is set as host-only (e.g. because
// the backend's ``COOKIE_DOMAIN`` / ``CORS_ORIGINS`` /
// ``CSP_CONNECT_SRCS`` env vars do not resolve to a shared eTLD+1),
// the browser still auto-attaches it on cross-site XHR — but
// ``document.cookie`` on the frontend origin cannot READ it. The
// previous PIN-login failure on Scaleway staging matched this exact
// pattern: browser sent the cookie, JS could not read it, no header
// was set, CSRF middleware returned 403 ``csrf_token_required``
// BEFORE ADMIN_PIN was ever validated.
//
// Fix: the backend now echoes the current token in the
// ``X-CSRF-Token`` response header on every /api response (see
// ws5_security.CSRFMiddleware) and CORS ``expose_headers`` allows
// same-frontend JS to read it. We cache the last-seen value here in
// memory and use it as a fallback when ``document.cookie`` returns
// nothing. No storage in localStorage / sessionStorage — the value
// lives only for the current page load.
let _lastCsrfTokenFromHeader = null;

// WS-8 — one-shot purge of any pre-WS-8 credential-shaped values that
// may linger in localStorage / sessionStorage from an older build. No
// bearer token is written or read anywhere on the client after WS-8.
if (typeof window !== "undefined") {
  purgeLegacyAuthCredentials();
}

// Request interceptor — WS-8 no longer attaches Authorization. Only
// the X-Locale UI preference and the WS-5 X-CSRF-Token header (on
// state-changing requests, if the double-submit cookie is present)
// are forwarded. Never a credential.
api.interceptors.request.use((config) => {
  if (typeof window !== "undefined") {
    try {
      const loc = localStorage.getItem("stockaily_locale");
      if (loc && !config.headers["X-Locale"]) {
        config.headers["X-Locale"] = loc;
      }
    } catch { /* swallow */ }
    // WS-5 — double-submit CSRF token. The server sets a NON-HttpOnly
    // ``stockaily_csrf`` cookie; we echo it in the header for every
    // unsafe method. Missing / mismatched -> 403 at the server.
    const method = String(config.method || "get").toUpperCase();
    if (method !== "GET" && method !== "HEAD" && method !== "OPTIONS") {
      try {
        let token = null;
        try {
          const m = document.cookie.match(/(?:^|;\s*)stockaily_csrf=([^;]+)/);
          if (m && m[1]) {
            token = decodeURIComponent(m[1]);
          }
        } catch { /* swallow */ }
        // WS-5 v3 — host-only-cookie fallback: use the token last
        // echoed by the backend in the X-CSRF-Token response header.
        if (!token && _lastCsrfTokenFromHeader) {
          token = _lastCsrfTokenFromHeader;
        }
        if (token && !config.headers["X-CSRF-Token"]) {
          config.headers["X-CSRF-Token"] = token;
        }
      } catch { /* swallow */ }
    }
  }
  return config;
});

// Response interceptor — request-id ring buffer + global 401 signal.
// EPF v1.3 — Detect JSON endpoints answered with an SPA `text/html`
// shell. This happens when `REACT_APP_BACKEND_URL` is missing / empty
// at build time and API calls fall back to the frontend origin, where
// nginx SPA-fallbacks them to `index.html` (HTTP 200, `Content-Type:
// text/html`). Axios then hands us the HTML string as `response.data`,
// consumers do `data.picks ?? []`, and every homepage market card
// silently renders empty.
//
// This guard makes the misconfiguration LOUD instead of invisible:
// any 2xx response with a `text/html` content-type is rejected with a
// synthetic client-side error and a bright console.error. No caller
// on the market-snapshot / homepage path ever consumes SPA HTML as if
// it were valid API JSON.
//
// The guard applies to every `api.*` call — same origin, cross-origin,
// success, and rewritten SPA fallback alike.
function _isHtmlSpaFallback(response) {
  const raw = response?.headers?.["content-type"] || response?.headers?.["Content-Type"] || "";
  const ct = String(raw).toLowerCase();
  return ct.includes("text/html");
}

api.interceptors.response.use(
  (response) => {
    // WS-5 v3 — cache the CSRF token echoed by the backend in the
    // ``X-CSRF-Token`` response header (see ws5_security.CSRFMiddleware).
    // Runs FIRST so a hard SPA-fallback reject below still leaves the
    // token cached from earlier successful responses (defensive).
    try {
      const tok =
        response?.headers?.["x-csrf-token"] ||
        response?.headers?.["X-CSRF-Token"] ||
        response?.headers?.get?.("x-csrf-token");
      if (typeof tok === "string" && tok.length > 0) {
        _lastCsrfTokenFromHeader = tok;
      }
    } catch { /* swallow */ }
    if (_isHtmlSpaFallback(response)) {
      const url = (response.config?.baseURL || "") + (response.config?.url || "");
      const originUsed = (typeof window !== "undefined" && window.location?.origin) || "";
      // eslint-disable-next-line no-console
      console.error(
        "[stockaily.api] api_returned_html reason=REACT_APP_BACKEND_URL_missing_or_wrong " +
        "path=%s effective_url=%s origin=%s backend_url=%s",
        response.config?.url || "?",
        url,
        originUsed,
        BACKEND_URL_RESOLVED,
      );
      const err = new Error("api_returned_html");
      err.name = "ApiHtmlFallbackError";
      err.code = "api_returned_html";
      err.status = response.status;
      err.request_url = response.config?.url || null;
      // Do NOT attach `response.data` (raw HTML) so accidental toString
      // in error boundaries doesn't dump kilobytes of markup.
      return Promise.reject(err);
    }
    // ---- (original success interceptor continues below) ----
    if (typeof window !== "undefined") {
      const reqId =
        response.headers?.["x-request-id"] ||
        response.headers?.get?.("x-request-id");
      if (reqId) {
        window.__STOCKAILY_LAST_REQUEST_IDS__ =
          window.__STOCKAILY_LAST_REQUEST_IDS__ || [];
        window.__STOCKAILY_LAST_REQUEST_IDS__.unshift({
          request_id: reqId,
          path: response.config?.url || "?",
          status: response.status,
          ts: new Date().toISOString(),
        });
        window.__STOCKAILY_LAST_REQUEST_IDS__ =
          window.__STOCKAILY_LAST_REQUEST_IDS__.slice(0, 20);
      }
    }
    return response;
  },
  (error) => {
    // WS-5 v3 — capture CSRF token also from error responses (e.g. the
    // 403 that first exposes the fresh token to the client). Enables
    // an immediate retry to succeed on the next unsafe request.
    try {
      const h = error?.response?.headers;
      const tok = h && (h["x-csrf-token"] || h["X-CSRF-Token"]);
      if (typeof tok === "string" && tok.length > 0) {
        _lastCsrfTokenFromHeader = tok;
      }
    } catch { /* swallow */ }
    if (typeof window !== "undefined" && error?.response?.headers) {
      const reqId = error.response.headers["x-request-id"];
      if (reqId) {
        window.__STOCKAILY_LAST_REQUEST_IDS__ =
          window.__STOCKAILY_LAST_REQUEST_IDS__ || [];
        window.__STOCKAILY_LAST_REQUEST_IDS__.unshift({
          request_id: reqId,
          path: error.config?.url || "?",
          status: error.response.status,
          ts: new Date().toISOString(),
          error: true,
        });
        window.__STOCKAILY_LAST_REQUEST_IDS__ =
          window.__STOCKAILY_LAST_REQUEST_IDS__.slice(0, 20);
      }
    }
    // WS-8 — on a global 401 (non-/auth/me, non-admin route), clear
    // the local auth-UI hint and broadcast so the AuthContext drops
    // ``user``. The credential itself lives in an HttpOnly cookie
    // and is not our concern here.
    if (typeof window !== "undefined" && error?.response?.status === 401) {
      const reqUrl = String(error.config?.url || "");
      const isAuthMe = reqUrl.endsWith("/auth/me");
      const path = reqUrl.split("?")[0];
      const isAdminRoute = path.includes("/admin/") || path.includes("/admin?");
      const ADMIN_ONLY_HEALTH_PATHS = [
        "/_health/llm_smoke_test",
      ];
      const isAdminOnlyHealth = ADMIN_ONLY_HEALTH_PATHS.some((p) => path.endsWith(p));
      if (!isAuthMe && !isAdminRoute && !isAdminOnlyHealth) {
        try { clearAuthHint(); } catch { /* swallow */ }
        try {
          window.dispatchEvent(
            new CustomEvent("stockaily:auth_failed", {
              detail: {
                from_url: reqUrl,
                request_id: error.response.headers?.["x-request-id"] || null,
                ts: new Date().toISOString(),
              },
            }),
          );
        } catch { /* CustomEvent unavailable */ }
      }
    }
    return Promise.reject(error);
  }
);

// Backwards-compat export retained for the small number of debug
// panels that still call ``looksLikeJwt``. WS-8 does not use this
// function for any credential decision.
export function looksLikeJwt(token) {
  if (typeof token !== "string" || token.length < 50) return false;
  const parts = token.split(".");
  if (parts.length !== 3) return false;
  return parts.every((p) => p.length > 0 && /^[A-Za-z0-9_-]+$/.test(p));
}

export default api;
